/**
 * @file me-api.ts
 * @description Typed wrappers for GET/PATCH /api/v1/me — every authed
 *   user (customer, staff, admin) uses these to read + update their
 *   own profile (full_name, phone, locale). Server enforces "self
 *   only" by reading the JWT subject; no userId is sent in the body.
 * @module Lib
 * @security Same JWT/refresh pipeline as the rest of the api wrapper.
 *   PII is encrypted at rest; the API decrypts on response.
 */

import { api, authenticatedFetch } from './api';

export interface MeProfile {
  id: string;
  email: string;
  role: 'admin' | 'staff' | 'customer';
  locale: 'he' | 'en';
  full_name: string | null;
  phone: string | null;
  /** Slice 77 — user's own account-created date + last sign-in. */
  created_at: string;
  last_login_at: string | null;
}

export interface UpdateMeInput {
  /** `null`/'' clear the field; a string sets it. */
  full_name?: string | null;
  phone?: string | null;
  locale?: 'he' | 'en';
}

export function getMe(): Promise<MeProfile> {
  return api<MeProfile>('/me', { method: 'GET' });
}

export function updateMe(input: UpdateMeInput): Promise<MeProfile> {
  return api<MeProfile>('/me', { method: 'PATCH', json: input });
}

export interface DashboardSummary {
  open_projects: number;
  completed_projects: number;
  pending_files: number;
  /** Slice 72 — customer's in-flight projects past desired_deadline. */
  overdue_projects: number;
}

export function getDashboardSummary(): Promise<DashboardSummary> {
  return api<DashboardSummary>('/me/dashboard', { method: 'GET' });
}

/**
 * GDPR Art. 15 — download the full export as a JSON file via the
 * browser. Goes around the `api()` helper because the response is a
 * file attachment (content-disposition), not a JSON envelope to parse.
 * Triggers a hidden `<a download>` so the file lands in the user's
 * Downloads folder named by the server's filename header.
 */
export async function exportMyData(): Promise<void> {
  const res = await authenticatedFetch('/me/export', {
    method: 'GET',
  });
  if (!res.ok) {
    throw new Error(`Export failed (${res.status})`);
  }
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const parsed = match?.[1];
  const filename =
    parsed !== undefined ? parsed : `dsar-export-${new Date().toISOString().slice(0, 10)}.json`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * GDPR Art. 17 — irreversible. Caller must collect confirmation in
 * the UI before invoking. Returns when the server has anonymized the
 * user row and revoked refresh tokens.
 */
export function deleteMyAccount(): Promise<void> {
  return api<void>('/me', { method: 'DELETE' });
}
